// toolGate.js — Fix 3 (code-tab-to-10).
// @arch:pure-engine
//
// Composes the two pre-dispatch deny checks into ONE pure decision so the spine seam
// stays a single call (keeps the dp-loc ratchet flat — the guardrail LOGIC lives here,
// in loop/, not in runLocalAgentLoopInner):
//   1. evaluateToolPolicy — the declarative per-tool/per-path deny-list (Camada V).
//   2. evaluateGuardrail   — file-deletion / external-write guardrail (deny-by-default).
// Policy has precedence. When guardrailEnabled=false the result is byte-identical to
// evaluateToolPolicy alone (the live default).
//
// PURE / framework-free: no react/window/IPC. The flag READ stays in the spine; this
// module only receives the resolved boolean.

import { evaluateToolPolicy } from '../toolPolicy.js';
import { evaluateGuardrail } from './toolGuardrail.js';

/**
 * @param {string} toolName
 * @param {object} args
 * @param {object} opts
 * @param {object|null} opts.toolPolicy        declarative deny policy (null = no policy)
 * @param {boolean}     opts.guardrailEnabled  guardrail on? (resolved in the spine)
 * @param {string}      opts.root              resolved project root
 * @param {{ denied: boolean, reason?: string }|null} [opts.preToolHookBlock] E8 — a
 *   pre-tool lifecycle hook's resolved veto (computed impurely in the spine via
 *   loop/hookLifecycle.js). null = no hook block. A hook can only ADD a deny here — it
 *   never removes policy/guardrail checks, and commandSafety still applies downstream.
 * @param {boolean} [opts.correctionMode] E15-T05 — turno de auto-correct (oracle RED)?
 * @returns {{ denied: boolean, reason?: string }}
 */
export function evaluateToolGate(toolName, args, { toolPolicy = null, guardrailEnabled = false, root = '', preToolHookBlock = null, correctionMode = false } = {}) {
  const pol = evaluateToolPolicy(toolName, args, toolPolicy);
  if (pol.denied) return pol;
  // harness-audit 07-13 — a PRÓPRIA policy de permissão (.apolo/permissions.json) era
  // editável pelo agente: 1 write relaxava todas as regras. Circuit-breaker determinístico
  // igual ao features.json: nunca auto-editável (nem via write tool, nem via shell — abaixo).
  const policyGuard = evaluatePolicyWriteProtect(toolName, args);
  if (policyGuard.denied) return policyGuard;
  // F13-E15-T05 (anti-reward-hacking) — durante o ciclo de correção do oracle,
  // editar TESTE pra fazê-lo passar é specification gaming: write-protect.
  const testGuard = evaluateTestWriteProtect(toolName, args, correctionMode);
  if (testGuard.denied) return testGuard;
  // Dente #1 (céticos 2026-07-06) — run_command era a PORTA DOS FUNDOS dos dois
  // write-protects: `echo x > .apolo/features.json` e `sed -i` no teste passavam.
  const cmdGuard = evaluateCommandWriteBypass(toolName, args, correctionMode);
  if (cmdGuard.denied) return cmdGuard;
  const guard = evaluateGuardrail(toolName, args, { enabled: guardrailEnabled, root });
  if (guard.denied) return { denied: true, reason: guard.reason };
  if (preToolHookBlock?.denied) return { denied: true, reason: preToolHookBlock.reason };
  return { denied: false };
}

// E15-T05 — shapes de arquivo de teste (vitest/jest/pytest/specs).
const TEST_PATH_RE = /(\.(test|spec)\.[jt]sx?$|\.test\.cjs$|__tests__\/|(^|\/)test_[^/]+\.py$)/i;
const WRITE_TOOL_RE = /^(write_file|apply_edit|multi_edit)$/;

// harness-audit 07-13 — a policy de permissão do projeto. Canônico `.apolo/`, com
// `.apollo/` legado (permissions.js aceita os dois). Protegida sempre (não só correctionMode).
const PERMISSIONS_TARGET_RE = /(^|[\s'"/=])\.?apoll?o\/permissions\.json/i;

/** Write-protect da própria policy de permissão via write tool (write_file/apply_edit/multi_edit). SEMPRE. */
export function evaluatePolicyWriteProtect(toolName, args) {
  if (!WRITE_TOOL_RE.test(String(toolName || ''))) return { denied: false };
  const path = String(args?.path || args?.file_path || '');
  if (!PERMISSIONS_TARGET_RE.test(path)) return { denied: false };
  return {
    denied: true,
    reason: 'a policy de permissão (.apolo/permissions.json) é IMUTÁVEL pelo agente — ela DEFINE o que você pode fazer, então relaxá-la em 1 write anularia o gate. Edite-a você mesmo fora da sessão, ou peça ao usuário.',
  };
}

/** Write-protect de testes SÓ em correctionMode (fora dele, editar teste é normal). */
export function evaluateTestWriteProtect(toolName, args, correctionMode) {
  if (!correctionMode || !WRITE_TOOL_RE.test(String(toolName || ''))) return { denied: false };
  const path = String(args?.path || args?.file_path || '');
  if (!TEST_PATH_RE.test(path)) return { denied: false };
  return {
    denied: true,
    reason: `Write-protect (anti-reward-hacking): você está num ciclo de CORREÇÃO do oracle — editar o TESTE (${path}) pra fazê-lo passar é burlar o juiz. Conserte o CÓDIGO que o teste cobre. Se o teste estiver genuinamente errado, DIGA isso na resposta final (o usuário decide).`,
  };
}

// ── Dente #1 — bypass de write-protect via shell ─────────────────────────────
// Heurística CONSERVADORA: leitura (cat/grep/jq/diff) passa; nega só quando o
// alvo protegido é destino de redirecionamento (`> alvo`, `>> alvo`) ou aparece
// no MESMO segmento (entre |;&) de um comando de escrita/remoção/restauração.
const FEATURES_TARGET_RE = /(^|[\s'"/=])\.?apolo\/features\.json|(^|[\s'"/=])features\.json/i;
const SEG_WRITE_CMD_RE = /(^|\s)(sudo\s+)?(tee|mv|cp|rm|truncate|dd|unlink|install)\b|\bsed\s+-\w*i\b|\bperl\s+-\w*i\b|\bgit\s+(checkout|restore)\b/;

function commandWritesToTarget(cmd, targetRe) {
  // 1. destino de redirecionamento: "> alvo" / ">> alvo" (exclui fd-dup ">&1")
  const redir = /([^<>]|^)>{1,2}(?!&)\s*(['"]?)([^\s'"|;&]+)/g;
  let m;
  while ((m = redir.exec(cmd)) !== null) {
    if (targetRe.test(m[3])) return true;
  }
  // 2. alvo dentro de um segmento com comando de escrita
  for (const seg of String(cmd).split(/[|;&]+/)) {
    if (SEG_WRITE_CMD_RE.test(seg) && targetRe.test(seg)) return true;
  }
  return false;
}

const TEST_TOKEN_RE = /(\.(test|spec)\.[cm]?[jt]sx?|__tests__\/|(^|\/)test_[^\s'"]+\.py)/i;

/** run_command que ESCREVE em alvo protegido: features.json (sempre) e arquivos de teste (correctionMode). */
export function evaluateCommandWriteBypass(toolName, args, correctionMode = false) {
  if (String(toolName) !== 'run_command') return { denied: false };
  const cmd = String(args?.command ?? args?.cmd ?? '');
  if (!cmd) return { denied: false };
  if (commandWritesToTarget(cmd, FEATURES_TARGET_RE)) {
    return { denied: true, reason: 'a feature list (.apolo/features.json) é IMUTÁVEL — nem via shell. A única mutação permitida é update_feature_status({"id","status","proof"})' };
  }
  // harness-audit 07-13 — mesma proteção da policy de permissão via shell (echo >, sed -i, tee…).
  if (commandWritesToTarget(cmd, PERMISSIONS_TARGET_RE)) {
    return { denied: true, reason: 'a policy de permissão (.apolo/permissions.json) é IMUTÁVEL pelo agente — nem via shell. Ela define o gate; relaxá-la o anularia.' };
  }
  if (correctionMode && commandWritesToTarget(cmd, TEST_TOKEN_RE)) {
    return { denied: true, reason: 'Write-protect (anti-reward-hacking): você está num ciclo de CORREÇÃO do oracle — reescrever o TESTE via shell é burlar o juiz. Conserte o CÓDIGO que o teste cobre' };
  }
  return { denied: false };
}
